import {useCallback, useEffect, useMemo, useReducer, useRef} from "react";
import styled from "styled-components";
import {Observer} from "mobx-react";
import {useAppStore} from "../../../StateManager";
import {XyiconFeature} from "../../../generated/api/base";
import {getActionBarButtonsForFeature} from "../modules/ActionBarUtils";
import type {IModel, ISpaceItemModel} from "../../../data/models/Model";
import type {Boundary} from "../../../data/models/Boundary";
import type {BoundarySpaceMap} from "../../../data/models/BoundarySpaceMap";
import {ArrayUtils} from "../../../utils/data/array/ArrayUtils";
import type {GridSide} from "../modules/spaceeditor/spaceselector/GridTypes";
import {ModuleViewV5} from "./ModuleViewV5";

/** Min, default, and max widths in pixels for SplitterV5 (data-min-width, data-default-width, data-max-width). */
const SIDE_GRID_MIN_PX = 512;
const SIDE_GRID_DEFAULT_PX = 696;
const SIDE_GRID_MAX_RIGHT_PX = 1240;

interface ISideGridProps {
	readonly side: GridSide;
	readonly itemFeature: XyiconFeature;
	readonly focusedItems: ISpaceItemModel[];
	readonly registerClosePopupsHandler?: (closeHandler: () => void) => void;
}

export const SideGrid = (props: ISideGridProps) => {
	const appState = useAppStore((state) => state.appState);
	const {spaceViewRenderer} = appState.app.graphicalTools;
	const {spaceItemController} = spaceViewRenderer;
	const {side, itemFeature, focusedItems, registerClosePopupsHandler} = props;
	const maxWidthPx = side === "right" ? SIDE_GRID_MAX_RIGHT_PX : 0;
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	const itemManager = spaceViewRenderer.getItemManager(itemFeature);
	const selectedItemsKeyRef = useRef<string>("");
	const scheduledForceUpdateFrameRef = useRef<number | null>(null);
	const moduleViewRef = useRef<ModuleViewV5>(null);

	const getSelectedItemsForSideGrid = useCallback((): IModel[] => {
		let selectedItems = itemManager.selectedItems.map((spaceItem) => spaceItem.modelData).filter((modelData) => !!modelData);

		if (itemFeature === XyiconFeature.Boundary) {
			selectedItems = ArrayUtils.removeDuplicates((selectedItems as BoundarySpaceMap[]).flatMap((b) => b.parent) as Boundary[]);
		}

		return selectedItems as IModel[];
	}, [itemFeature, itemManager]);

	const getSelectedItemsKey = useCallback(() => {
		return getSelectedItemsForSideGrid()
			.map((item) => item.id)
			.toSorted()
			.join(",");
	}, [getSelectedItemsForSideGrid]);

	const scheduleSelectionUpdate = useCallback(() => {
		const nextSelectedItemsKey = getSelectedItemsKey();

		if (selectedItemsKeyRef.current === nextSelectedItemsKey) {
			return;
		}

		selectedItemsKeyRef.current = nextSelectedItemsKey;

		if (scheduledForceUpdateFrameRef.current !== null) {
			return;
		}

		scheduledForceUpdateFrameRef.current = window.requestAnimationFrame(() => {
			scheduledForceUpdateFrameRef.current = null;
			forceUpdate();
		});
	}, [getSelectedItemsKey]);

	const setSelectedItems = useCallback(
		(selectedItems: IModel[]) => {
			spaceItemController.deselectAll(false, true);

			for (const selectedItem of selectedItems) {
				if (selectedItem.ownFeature === itemFeature) {
					if (itemFeature === XyiconFeature.Boundary) {
						for (const boundarySpaceMap of (selectedItem as Boundary).boundarySpaceMaps) {
							const item3D = itemManager.getItemById(boundarySpaceMap.id);
							item3D?.select();
						}
					} else {
						const item3D = itemManager.getItemById(selectedItem.id);
						item3D?.select(true, true);
					}
				}
			}

			spaceItemController.updateActionBar(true, true);
			// Some action bar changes emit a signal; others do not. Both paths use
			// the same pending frame so a grid click causes one selection update.
			scheduleSelectionUpdate();
		},
		[itemManager, itemFeature, spaceItemController, scheduleSelectionUpdate],
	);

	const setFocusedHandler = useCallback(
		(focusedItems: ISpaceItemModel[]) => {
			spaceViewRenderer.inheritedMethods.focusItems(focusedItems);
		},
		[spaceViewRenderer],
	);

	useEffect(() => {
		setFocusedHandler([]);
	}, [itemFeature, setFocusedHandler]);

	const actionBarUpdatedSignal = spaceItemController.signals.actionBarUpdated;
	const canvasItemClickedSignal = spaceItemController.signals.canvasItemClicked;
	const scrollToCanvasItem = useCallback(
		(item: ISpaceItemModel) => {
			if (item.ownFeature !== itemFeature) {
				return;
			}
			const rowItem = itemFeature === XyiconFeature.Boundary ? ((item as BoundarySpaceMap).parent ?? item) : item;
			moduleViewRef.current?.scrollToItem(rowItem);
		},
		[itemFeature],
	);

	useEffect(() => {
		// A repeated canvas click is navigation intent even when the selected IDs
		// are unchanged. Scroll directly rather than forcing another grid render.
		canvasItemClickedSignal.add(scrollToCanvasItem);
		return () => {
			canvasItemClickedSignal.remove(scrollToCanvasItem);
		};
	}, [canvasItemClickedSignal, scrollToCanvasItem]);

	useEffect(() => {
		selectedItemsKeyRef.current = getSelectedItemsKey();
		actionBarUpdatedSignal.add(scheduleSelectionUpdate);

		return () => {
			actionBarUpdatedSignal.remove(scheduleSelectionUpdate);

			if (scheduledForceUpdateFrameRef.current !== null) {
				window.cancelAnimationFrame(scheduledForceUpdateFrameRef.current);
				scheduledForceUpdateFrameRef.current = null;
			}
		};
	}, [actionBarUpdatedSignal, getSelectedItemsKey, scheduleSelectionUpdate]);

	const actionBarButtons = useMemo(() => getActionBarButtonsForFeature(itemFeature, appState), [appState, itemFeature]);

	return (
		<Observer>
			{() => {
				const selectedItems = getSelectedItemsForSideGrid();

				return (
					<SideGridStyled
						data-min-width={SIDE_GRID_MIN_PX}
						data-default-width={SIDE_GRID_DEFAULT_PX}
						{...(maxWidthPx > 0 ? {"data-max-width": maxWidthPx} : {})}
					>
						<ModuleViewV5
							ref={moduleViewRef}
							feature={itemFeature}
							actionBar={actionBarButtons}
							selectedItems={selectedItems}
							setSelectedItems={setSelectedItems}
							focusedItems={focusedItems}
							setFocusedItems={setFocusedHandler}
							manageDetailsPanel={false}
							isSideGrid={true}
							registerClosePopupsHandler={registerClosePopupsHandler}
						/>
					</SideGridStyled>
				);
			}}
		</Observer>
	);
};

const SideGridStyled = styled.div`
	position: relative;
`;
